import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { ContactLinkProps } from '~/components/UI/Buttons/ContactLink/ContactLink'
import { useMemo } from 'react'
import LinkedIn from '~/assets/icons/linkedin'
import Whatsapp from '~/assets/icons/whatsapp'
import Email from '~/assets/icons/email'
import Phone from '~/assets/icons/phone'
import Form from '~/assets/icons/form'
import Callback from '~/assets/icons/callback'
import LiveChat from '~/assets/icons/live-chat'
import World from '~/assets/icons/world'
import Messages from '~/assets/icons/messages'
import { CardProps } from '~/components/types/card'

const cardVariants = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'bg-blue-d text-white',
      'mid-blue': 'bg-blue-m text-white',
      chartreuse: 'bg-char text-blue-d',
      'light-grey': 'bg-grey-l text-blue-d',
      white: 'bg-white text-blue-d'
    },
    hover: {
      'ink-blue': 'hover:bg-blue-d-60 hover:[&>a]:text-white',
      'mid-blue': 'hover:bg-[#173DC2] hover:[&>a]:text-white',
      chartreuse: 'hover:bg-char-60 hover:[&>a]:text-blue-d',
      'light-grey': 'hover:bg-white hover:[&>a]:text-blue-d',
      white: 'hover:bg-grey-lhover:[&>a]:text-blue-d'
    }
  },
  defaultVariants: {
    backgroundColour: 'ink-blue'
  }
})

export type MultiContactCardProps = CardProps & {
  content: {
    contactLinks: Array<ContactLinkProps['data']>
  }
}

const ContactCardLink = ({
  item,
  backgroundColour,
  foregroundColour,
  carousel
}: {
  item: ContactLinkProps['data']
  backgroundColour: string
  foregroundColour: string
  carousel: boolean | undefined
}) => {
  const linkType = useMemo(() => {
    switch (item.type) {
      case 'phone':
        return {
          icon: (
            <Phone
              fill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Phone'}
            />
          ),
          link: `tel:${item.link.replace(/\s+|\(.*?\)/g, '')}`
        }
      case 'email':
        return {
          icon: (
            <Email
              fill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Email'}
            />
          ),
          link: `mailto:${item.link}`
        }
      case 'text':
        return {
          icon: (
            <Messages
              fill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Messages'}
            />
          ),
          link: `sms:${item.link}`
        }
      case 'live-chat':
        return {
          icon: (
            <LiveChat
              fill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'LiveChat'}
            />
          ),
          link: item.link
        }
      case 'enquiry-form':
        return {
          icon: (
            <Form
              fill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Enquiry'}
            />
          ),
          link: item.link
        }
      case 'callback':
        return {
          icon: (
            <Callback
              fill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Callback'}
            />
          ),
          link: item.link
        }
      case 'whatsapp':
        return {
          icon: (
            <Whatsapp
              fill={foregroundColour}
              secondaryfill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'WhatsApp'}
            />
          ),
          link: `https://wa.me/${item.link.replace(/\s+|\(.*?\)/g, '')}`
        }
      case 'linkedin':
        return {
          icon: (
            <LinkedIn
              fill={foregroundColour}
              secondaryfill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'LinkedIn'}
            />
          ),
          link: item.link
        }
      case 'international':
        return {
          icon: (
            <World
              fill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'World'}
            />
          ),
          link: `tel:${item.link.replace(/\s+|\(.*?\)/g, '')}`
        }

      default:
        return {
          icon: (
            <Phone
              fill={foregroundColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Phone'}
            />
          ),
          link: item.link
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.type])

  return (
    <a href={linkType.link} className='unstyled-link m-0'>
      <span
        className={cn(
          'gap-xs mx-xs-plus sm:mx-s lg:mx-m flex items-center',
          backgroundColour === 'mid-blue'
            ? 'border-blue-m-60'
            : 'border-blue-d-60',
          foregroundColour === 'white'
            ? 'text-white [&_svg]:stroke-white'
            : 'text-blue-d [&_svg]:stroke-blue-d',
          carousel ? 'py-xs' : 'py-s'
        )}
      >
        <div className='self-end'>{linkType.icon}</div>
        {item.label}
      </span>
    </a>
  )
}

const MultiContactCard = ({
  content,
  className,
  carousel
}: MultiContactCardProps) => {
  const { backgroundColour, heading, contactLinks } = content

  const foregroundColour = ['ink-blue', 'mid-blue'].includes(backgroundColour)
    ? 'white'
    : 'ink-blue'

  return (
    <div
      className={cn(
        cardVariants({ backgroundColour: backgroundColour, hover: null }),
        className,
        'py-xs-plus sm:py-s lg:py-m gap-s relative flex !h-[500px] max-h-[500px] flex-col'
      )}
    >
      <h3 className='px-xs-plus sm:px-s lg:px-m'>{heading}</h3>
      <div className='flex h-full items-center'>
        <ul className='w-full list-none overflow-y-auto pl-0'>
          {Array.isArray(contactLinks) &&
            contactLinks.map((item, index) => (
              <li
                key={index}
                className={cn(
                  cardVariants({
                    backgroundColour: null,
                    hover: backgroundColour
                  }),
                  'transition-colors duration-500 not-last:[&_span]:border-b'
                )}
              >
                <ContactCardLink
                  item={item}
                  backgroundColour={backgroundColour}
                  foregroundColour={foregroundColour}
                  carousel={carousel}
                />
              </li>
            ))}
        </ul>
      </div>
    </div>
  )
}

export default MultiContactCard
